import { Link } from "react-router-dom";
import { ArrowRight, Clock, Facebook, Instagram, Mail, MapPin, Phone, Youtube } from "lucide-react";
import { useContent } from "@/context/ContentContext";

// Royal Signature footer — rich obsidian footer: brand + tagline, Explore,
// Contact, Opening Hours with Plan Your Visit, then the slim reference bar.
export const RoyalFooter = () => {
  const { content } = useContent();
  const s = content?.settings || {};

  const explore = [
    { to: "/treatments", label: "Treatments" },
    { to: "/doctors", label: "Our Doctors" },
    { to: "/about", label: "About Royal Skin" },
    { to: "/gallery", label: "Results Gallery" },
    { to: "/blog", label: "Skin Journal" },
    { to: "/contact", label: "Contact & Booking" },
  ];

  return (
    <footer className="bg-[#0F0F0F]" data-testid="royal-footer">
      {/* rich detail section */}
      <div className="mx-auto grid max-w-[1400px] gap-12 px-4 py-16 sm:px-8 md:grid-cols-2 lg:grid-cols-4 lg:px-12">
        <div>
          {s.brand_lockup_url ? (
            <img src={s.brand_lockup_url} alt="Royal Skin Cosmetic Clinic" className="h-20 w-auto object-contain" />
          ) : (
            <p className="leading-none">
              <span className="block font-serif text-lg font-medium uppercase tracking-[0.28em] text-white">Royal</span>
              <span className="mt-1 block text-[8px] font-semibold uppercase tracking-[0.42em] text-[#8A8580]">Skin Clinic</span>
            </p>
          )}
          {s.tagline && (
            <p className="mt-5 max-w-xs text-sm font-light leading-relaxed text-[#8A8580]" data-testid="footer-tagline">
              {s.tagline}
            </p>
          )}
        </div>

        <div>
          <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-[#C5A059]">Explore</p>
          <ul className="mt-5 space-y-3">
            {explore.map((l) => (
              <li key={l.to}>
                <Link
                  to={l.to}
                  data-testid={`footer-explore-${l.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className="text-sm font-light text-[#A9A29A] transition-colors hover:text-white"
                >
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-[#C5A059]">Contact</p>
          <ul className="mt-5 space-y-4 text-sm font-light text-[#A9A29A]">
            {s.phone && (
              <li>
                <a href={`tel:${s.phone.replace(/[^+\d]/g, "")}`} data-testid="footer-phone-link" className="flex gap-3 transition-colors hover:text-white">
                  <Phone className="mt-0.5 h-4 w-4 shrink-0 text-[#C5A059]" aria-hidden="true" />
                  {s.phone}
                </a>
              </li>
            )}
            {s.email && (
              <li>
                <a href={`mailto:${s.email}`} data-testid="footer-email-link" className="flex gap-3 transition-colors hover:text-white">
                  <Mail className="mt-0.5 h-4 w-4 shrink-0 text-[#C5A059]" aria-hidden="true" />
                  {s.email}
                </a>
              </li>
            )}
            {s.address && (
              <li className="flex gap-3">
                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-[#C5A059]" aria-hidden="true" />
                <span data-testid="footer-address">{s.address}</span>
              </li>
            )}
          </ul>
        </div>

        <div>
          <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-[#C5A059]">Opening Hours</p>
          {s.hours && (
            <p className="mt-5 flex gap-3 text-sm font-light leading-relaxed text-[#A9A29A]" data-testid="footer-hours">
              <Clock className="mt-0.5 h-4 w-4 shrink-0 text-[#C5A059]" aria-hidden="true" />
              {s.hours}
            </p>
          )}
          {s.address ? (
            <a
              href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(s.address)}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-plan-visit-link"
              className="mt-7 inline-flex items-center gap-2 rounded-[5px] border border-[#D4AF37]/60 px-6 py-3 text-[10px] font-semibold uppercase tracking-[0.15em] text-[#D4AF37] transition-all duration-300 hover:bg-[#D4AF37] hover:text-[#0F0F0F]"
            >
              Plan Your Visit <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
            </a>
          ) : (
            <Link
              to="/contact"
              data-testid="footer-plan-visit-link"
              className="mt-7 inline-flex items-center gap-2 rounded-[5px] border border-[#D4AF37]/60 px-6 py-3 text-[10px] font-semibold uppercase tracking-[0.15em] text-[#D4AF37] transition-all duration-300 hover:bg-[#D4AF37] hover:text-[#0F0F0F]"
            >
              Plan Your Visit <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
            </Link>
          )}
        </div>
      </div>

      {/* slim reference bar */}
      <div className="border-t border-[#1E1E1E]">
        <div className="mx-auto flex max-w-[1400px] flex-col items-center justify-between gap-5 px-4 py-6 sm:px-8 md:flex-row lg:px-12">
          <Link to="/" data-testid="footer-logo-link" aria-label="Royal Skin Clinic home" className="shrink-0">
            {s.logo_url ? (
              <img src={s.logo_url} alt="" className="h-9 w-auto object-contain" />
            ) : (
              <span className="font-serif text-sm font-medium uppercase tracking-[0.28em] text-white">Royal</span>
            )}
          </Link>
          <nav className="flex flex-wrap items-center justify-center gap-x-7 gap-y-2" aria-label="Footer">
            {[
              { to: "/", label: "Home" },
              { to: "/doctors", label: "About Dr Javeria" },
              { to: "/treatments", label: "Treatments" },
              { to: "/gallery", label: "Results" },
              { to: "/blog", label: "Journal" },
              { to: "/contact", label: "Contact" },
            ].map((l) => (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`footer-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="text-[10px] font-medium uppercase tracking-[0.18em] text-[#8A8580] transition-colors hover:text-white"
              >
                {l.label}
              </Link>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            {s.instagram && (
              <a href={s.instagram} target="_blank" rel="noopener noreferrer" data-testid="footer-instagram-link" aria-label="Instagram" className="text-[#8A8580] transition-colors hover:text-[#C5A059]">
                <Instagram className="h-4 w-4" aria-hidden="true" />
              </a>
            )}
            <span className="text-[#2A2A2A]" aria-hidden="true">
              <Facebook className="h-4 w-4" aria-hidden="true" />
            </span>
            <span className="text-[#2A2A2A]" aria-hidden="true">
              <Youtube className="h-4 w-4" aria-hidden="true" />
            </span>
          </div>
        </div>
      </div>
      <div className="border-t border-[#1E1E1E]">
        <p className="mx-auto max-w-[1400px] px-4 py-4 text-center text-[10px] font-light tracking-wide text-[#57534E] sm:px-8" data-testid="footer-copyright">
          © {new Date().getFullYear()} {s.clinic_name || "Royal Skin Clinic"}. All rights reserved.
        </p>
      </div>
    </footer>
  );
};
